import React, { useState, useEffect, useMemo } from "react";
import Select from 'react-select';

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple, { filterSelectStyles } from "../../components/FilterSimple";
import DotStatus from "../../components/DotStatus";
import UserCell from "../../components/UserCell";
import ModalCreateQuote from "./ModalCreateQuote";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { convertIntToMoney } from "../../helpers/conversions";
import { ModalDelete } from "../../components/ModalDelete";
import { canSeeBrands, hasPermission, hasQuoteDeletePermission } from "../../services/auth";
import { ModalFilter } from "./ModalFilter";
import Pagination from "../../components/Pagination";
import InputCheckbox from "../../components/InputCheckbox";
import ButtonCheckbox from "../../components/ButtonCheckbox";
import { toast } from "react-toastify";
import SelectStatus from "../../components/selects/SelectStatus";
import { createOptions, createStatusOptions } from "../../helpers/selects";
import InputSelect from "../../components/InputSelect";
import { ModalExport } from "../../components/ModalExport";

const SelectStatusList = ({quote, statuses}) => {
    const [loading, setLoading] = useState(false);
    const [currentStatusId, setCurrentStatusId] = useState(quote.status.id);

    const updateQuoteStatus = (quoteId, statusId) => {
        setLoading(true);

        api.put(`/budget/${quoteId}/status`, {
            status_id: statusId
        }).then(res => {
            setCurrentStatusId(statusId);
            toast.success('Status atualizado');
        }).catch(error => {
            renderError(error);
        }).then(() => {
            setLoading(false);
        });
    }

    return (
        <div className="select-status-list">
            <InputSelect
                loading={loading}
                label={`Status`}
                options={createStatusOptions(statuses)} 
                value={currentStatusId} 
                change={statusId => updateQuoteStatus(quote.id, statusId)}
            />
        </div>
    );
}

const quoteSearchParams = [
    {name: 'Nome da empresa', key: '[related][company.trade_name]', operation: 'contain', type: 'text' },
    {name: 'Razão social', key: '[related][company.company_name]', operation: 'contain', type: 'text' },
    {name: 'CNPJ', key: '[related][company.cnpj]', operation: 'contain', type: 'text' },
    {name: 'Código do orçamento', key: '[code]', operation: 'equal', type: 'text' },
    {name: 'Código da empresa', key: '[related][company.code]', operation: 'equal', type: 'text' },
    {name: 'Nome do responsável', key: '[related][admin.name]', operation: 'contain', type: 'text' },
    {name: 'Nome da pessoa', key: '[related][customer.name]', operation: 'contain', type: 'text' },
    {name: 'E-mail da pessoa', key: '[related][customer.email]', operation: 'contain', type: 'email' },
    {name: 'Marca', key: '[related][brand.name]', operation: 'contain', type: 'text' },
    {name: 'Origem', key: '[origin]', operation: 'contain', type: 'text' }
];

// Avaliado na chamada, nunca no import: o módulo carrega antes do login, quando
// o localStorage ainda não tem usuário nem plano.
const getQuoteSearchParams = () => canSeeBrands()
    ? quoteSearchParams
    : quoteSearchParams.filter(param => param.key !== '[related][brand.name]');

const getQuoteSearchParam = field => (
    quoteSearchParams.find(param => param.key === field) || quoteSearchParams[0]
);

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parseQuoteListFilters = search => {
    const params = new URLSearchParams(search);
    const searchParam = getQuoteSearchParam(params.get('search_field'));
    const proposal = params.get('proposal');
    const statusIds = params.get('statuses');

    return {
        page: normalizePage(params.get('page')),
        status: params.get('status') || null,
        statuses: statusIds ? statusIds.split(',').filter(Boolean) : [],
        searchField: searchParam.key,
        searchOperation: params.get('search_operation') || searchParam.operation || 'contain',
        search: params.get('search') || '',
        brand: params.get('brand') || null,
        adminId: params.get('admin_id') || null,
        createdFrom: params.get('created_from') || null,
        createdTo: params.get('created_to') || null,
        proposal: ['missing', 'generated'].includes(proposal) ? proposal : null,
        proposalCreatedFrom: params.get('proposal_created_from') || null,
        proposalCreatedTo: params.get('proposal_created_to') || null,
    };
};

const normalizeStartDate = value => {
    if (!value) return null;
    return value.includes(':') ? value : `${moment(value).format('YYYY-MM-DD')} 00:00:00`;
};

const normalizeEndDate = value => {
    if (!value) return null;
    return value.includes(':') ? value : `${moment(value).format('YYYY-MM-DD')} 23:59:59`;
};

const buildBudgetApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') {
        params.append(`filters[0]${filters.searchField}[${filters.searchOperation}]`, filters.search.trim());
    }

    if (filters.statuses.length > 0) {
        params.append('status_ids', filters.statuses.join(','));
    } else if (filters.status) {
        params.append('filters[0][related][status.id][equal]', filters.status);
    }

    if (filters.brand === 'without_brand') {
        params.append('filters[0][brand_id][is_null]', 1);
    } else if (filters.brand) {
        params.append('filters[0][brand_id][equal]', filters.brand);
    }

    if (filters.adminId) {
        params.append('filters[0][admin_id][equal]', filters.adminId);
    }

    if (filters.createdFrom) {
        params.append('filters[0][created_at][bigger_or_equal]', normalizeStartDate(filters.createdFrom));
    }

    if (filters.createdTo) {
        params.append('filters[0][created_at][less_or_equal]', normalizeEndDate(filters.createdTo));
    }

    if (filters.proposal) {
        params.append('proposal_filter', filters.proposal);
    }

    if (filters.proposalCreatedFrom) {
        params.append('proposal_created_from', normalizeStartDate(filters.proposalCreatedFrom));
    }

    if (filters.proposalCreatedTo) {
        params.append('proposal_created_to', normalizeEndDate(filters.proposalCreatedTo));
    }

    params.append('page', filters.page);

    return params.toString();
};

const buildQuoteListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.status) params.set('status', filters.status);
    if (filters.statuses.length > 0) params.set('statuses', filters.statuses.join(','));
    if (filters.search.trim() !== '') {
        params.set('search_field', filters.searchField);
        params.set('search_operation', filters.searchOperation);
        params.set('search', filters.search.trim());
    }
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.adminId) params.set('admin_id', filters.adminId);
    if (filters.createdFrom) params.set('created_from', filters.createdFrom);
    if (filters.createdTo) params.set('created_to', filters.createdTo);
    if (filters.proposal) params.set('proposal', filters.proposal);
    if (filters.proposalCreatedFrom) params.set('proposal_created_from', filters.proposalCreatedFrom);
    if (filters.proposalCreatedTo) params.set('proposal_created_to', filters.proposalCreatedTo);

    return params.toString();
};

const hasActiveValue = value => value !== null && value !== undefined && value !== '';

const countActiveQuoteFilters = filters => ([
    filters.search.trim(),
    filters.status || filters.statuses.length > 0,
    filters.brand,
    filters.adminId,
    filters.createdFrom || filters.createdTo,
    filters.proposal,
    !filters.proposal && (filters.proposalCreatedFrom || filters.proposalCreatedTo),
].filter(hasActiveValue).length);

export const QuoteList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const quoteFilters = useMemo(() => parseQuoteListFilters(location.search), [location.search]);
    const activeFilterCount = useMemo(() => countActiveQuoteFilters(quoteFilters), [quoteFilters]);

    const [loading, setLoading] = useState(true);
    const [showExport, setShowExport] = useState(false);
    const [showFilter, setShowFilter] = useState(false);

    const [quotes, setQuotes] = useState([]);
    const [data, setData] = useState({header: [], rows: []});
    const [showCreateQuote, setShowCreateQuote] = useState(false);
    const [quoteToDelete, setQuoteToDelete] = useState(null);

    const [statuses, setStatuses] = useState([]);
    const [brands, setBrands] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] = useState(false);

    useEffect(() => {
        getStatuses();
        getBrands();
    }, []);

    useEffect(() => {
        getQuotes();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [quotes, location.search]);

    const getStatuses = () => {
        api.get(`/status?filters[0][type][equal]=budget`).then(res => {
            setStatuses(res.data.resources);
        });
    }

    const getBrands = () => {
        if (!canSeeBrands()) return;

        api.get(`/brand?per_page=100`).then(res => {
            setBrands(res.data.resources?.data ?? res.data.resources ?? []);
        }).catch(() => setBrands([]));
    }

    const getQuotes = () => {
        setLoading(true);
        api.get(`/budget/paginate?${buildBudgetApiQuery(quoteFilters)}`).then(res => {
            // Tolerante a respostas fora do shape {data, pagination} (ex.: modo mock)
            const resources = res.data.resources ?? {};
            const pagination = resources.pagination ?? {};
            setQuotes(resources.data ?? []);
            setCurrentPage(pagination.current_page ?? 1);
            setLastPage(pagination.last_page ?? 1)
            setPerPage(pagination.per_page ?? 15)
            setTotalResults(pagination.total ?? 0)
            setHasNextPage((pagination.next_page_url ?? null) !== null);
            setHasPrevPage((pagination.prev_page_url ?? null) !== null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const replicateQuote = id => {
        setLoading(true);

        api.post(`/budget/replicate/${id}`).then(res => {
            toast.success('Orçamento duplicado com sucesso');
            getQuotes();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const generateData = () => {
        const showBrand = canSeeBrands();

        const header = [
            'Código',
            'Data da solicitação',
            'Nome',
            ...(showBrand ? ['Marca'] : []),
            'Pessoa',
            'Pedido',
            'Total',
            'Responsável',
            'Origem',
            'Status',
            '',
            ''
        ];
        const rows = [];
        
        quotes.map(quote => {
            let buttons = [];
            const returnTo = encodeURIComponent(`/quote/list${location.search}`);

            // buttons.push(
            //     <SelectStatusList statuses={statuses} quote={quote} />
            // );

            buttons.push(<Button type={`delete`} svg={`copy`} action={() => replicateQuote(quote.id)} />);
            
            if(hasQuoteDeletePermission(quote)){
                buttons.push(<Button type={`delete`} svg={`delete`} action={() => setQuoteToDelete(quote.id)} />)
            }

            rows.push({
                data: [
                    quote.code,
                    moment(quote.created_at).format('DD/MM/YYYY HH:mm'),
                    quote?.company_trade_name,
                    ...(showBrand ? [quote?.brand_name ?? quote?.brand?.name ?? '-'] : []),
                    quote?.customer_name,
                    quote.order ? <Link target={`_blank`} to={`/order/edit/${quote.order.id}`}>{quote.order.code}</Link> : '',
                    `R$ ${convertIntToMoney(quote.total)}`,
                    <UserCell name={quote?.admin_name} photo={quote?.admin_photo_url} />,
                    quote?.origin,
                    <>
                        <DotStatus color={quote?.status_color} size={15} />
                        {quote?.status_name}
                    </>,
                ],
                buttons,
                to: `/quote/edit/${quote.id}?return_to=${returnTo}`,
                id: hasQuoteDeletePermission(quote) ? quote.id : null
            });
        });

        setData({header, rows});
    }

    const finishCreation = () => {
        getQuotes();
    }

    const deleteQuote = () => {
        setLoading(true);

        api.delete(`/budget/${quoteToDelete}`).then(res => {
            getQuotes();
            setQuoteToDelete(null);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const deleteMultipleQuotes = () => {
        setLoading(true);

        api.delete(`/budget/multiple`, {
            data: {
                ids: selectedIds
            }
        }).then(res => {
            setSelectedIds([]);
            getQuotes();
            setShowConfirmMultipleDelete(false);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const updateQuoteListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...quoteFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || quoteFilters.page),
        };
        const search = buildQuoteListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }

    const handleSetFilter = (value, metadata) => {
        updateQuoteListUrl({
            searchField: metadata?.field || quoteFilters.searchField,
            searchOperation: metadata?.operation || quoteFilters.searchOperation,
            search: metadata?.value ?? '',
        });
    }

    const handleSetCustomFilters = value => {
        updateQuoteListUrl({
            brand: value.brand,
            adminId: value.adminId,
            createdFrom: value.createdFrom,
            createdTo: value.createdTo,
            proposal: value.proposal,
            proposalCreatedFrom: value.proposalCreatedFrom,
            proposalCreatedTo: value.proposalCreatedTo,
        });
    }

    const handleSetStatus = statusId => {
        updateQuoteListUrl({ status: statusId, statuses: [] });
    }

    const handleSetPage = page => {
        updateQuoteListUrl({ page }, false);
    }

    const handleSetBrand = brand => {
        updateQuoteListUrl({ brand });
    }

    const brandOptions = useMemo(() => brands.length > 0 ? [
        { label: 'Todas as marcas', value: null },
        { label: 'Sem marca', value: 'without_brand' },
        ...createOptions(brands, 'name', 'id')
    ] : [], [brands]);

    return (
        <>
            {showCreateQuote &&
                <ModalCreateQuote
                finishCreation={finishCreation}
                    close={() => setShowCreateQuote(false)}
                />
            }
            <ModalExport
                showModal={showExport}
                setShowModal={setShowExport}
                url={`${process.env.REACT_APP_URL}/budget/export`}
            />
            <ModalDelete
                show={quoteToDelete !== null}
                action={deleteQuote}
                loading={loading}
                close={() => setQuoteToDelete(null)}
            />
            <ModalDelete
                show={showConfirmMultipleDelete}
                text={<p>Tem certeza que deseja apagar <strong>{selectedIds.length} {selectedIds.length > 1 ? 'orçamentos' : 'orçamento'}</strong>?</p>}
                action={deleteMultipleQuotes}
                loading={loading}
                close={() => setShowConfirmMultipleDelete(false)}
            />
            <PanelTemplate id="quote-list">
                <ModalFilter
                    showFilter={showFilter}
                    setShowFilter={setShowFilter}
                    filters={quoteFilters}
                    setCustomFilters={handleSetCustomFilters}
                    brandOptions={brandOptions}
                />
                <PageHeader
                    left={
                        <>
                            <FilterSimple 
                                params={getQuoteSearchParams()}
                                filter={handleSetFilter}
                                externalValue={quoteFilters.search}
                                externalOption={quoteFilters.searchField}
                            />
                            {brands.length > 0 &&
                                <div className="filter-select">
                                    <Select
                                        placeholder="Escolha uma marca..."
                                        options={brandOptions}
                                        onChange={e => handleSetBrand(e.value)}
                                        value={
                                            brandOptions.find(option => String(option.value) === String(quoteFilters.brand))
                                                ?? { label: 'Marca selecionada', value: quoteFilters.brand }
                                        }
                                        styles={filterSelectStyles}
                                    />
                                </div>
                            }
                        </>
                    }

                    right={
                        <>
                            {selectedIds.length > 0 &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                svg={`delete`}
                                text={`Excluir ${selectedIds.length} selecionados`} 
                                action={() => setShowConfirmMultipleDelete(true)}  
                                />
                            }
                            {hasPermission('export_budget') &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                text={`Exportar orçamentos`} 
                                action={() => setShowExport(true)}  
                                />
                            }
                            <Button type={`secondary`} size={`small`} text={`Filtrar`} svg="filter" badge={activeFilterCount} action={() => setShowFilter(true)} />
                            {hasPermission('create_budget') &&    
                                <Button 
                                    type={`primary`}
                                    size={`small`}
                                    text={`Cadastrar orçamento`}
                                    svg="plus-white"
                                    action={() => setShowCreateQuote(true)}
                                />
                            }
                        </>
                    }
                />
                <ul className="filter-tab">
                    <li>
                        <a href="#" className={quoteFilters.status === null && quoteFilters.statuses.length === 0 ? 'active' : ''} onClick={e => {
                        e.preventDefault();
                        handleSetStatus(null);
                    }}>Todos</a></li>
                    {statuses.map((status, index) => (
                        <li key={index}>
                            <a href="#" className={quoteFilters.statuses.length === 0 && quoteFilters.status === status.id ? 'active' : ''} onClick={e => {
                                e.preventDefault();
                                handleSetStatus(status.id);
                            }}>
                                {status.name}
                            </a>
                        </li>
                    ))}
                </ul>
                <Box className={`no-padding with-pagination with-filter-inline`}>
                    <Table 
                        header={data.header}
                        rows={data.rows}
                        checks={true}
                        loading={loading}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
}

export default QuoteList;
